/**
 * Game state — fetches the player's Character/Health/Inventory/Equipment
 * sheets on entering the settlement and keeps them in sync via GameGateway's
 * socket pushes. Server-authoritative: this module never computes gameplay
 * state itself, only extrapolates a *display* value for energy between
 * fetches (S6 acceptance criterion: "energy displays optimistically
 * client-side and reconciles to server").
 *
 * Stat sourcing matters here (see character/equipment GDDs + S5 notes):
 * - Attributes (STR/INT/STA/AGI/PER) come from `character.attributes` — never blended.
 * - Derived stats for display come from `equipment.blendedStats` (equip changes
 *   nothing on `character.stats`, which stays base-only by design).
 * - Energy regen rate for extrapolation uses `character.stats.energyRegenPerMin`
 *   (base) — CharacterService computes energy with no equipment input, so
 *   extrapolating at the blended rate would drift and snap backward on reconcile.
 * - HP bar denominator is `health.maxHP`, not `blendedStats.hpAmount` — Health
 *   owns current/max HP; the two can disagree (S3's deferred gear-HP hookup),
 *   that's a known gap, not something to reconcile client-side.
 */
import { createStore } from '../../core/state/store';
import { api, ApiError } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import type {
  CharacterSheet, HealthSheet, InventorySheet, EquipmentSheet, EquipmentSlot, AttributeCode, UseItemResult,
  EnergyRefillResult,
} from './game-state.types';

export interface InventoryRejection {
  reason: string;
  message?: string;
}

export interface GameState {
  /**
   * `uncreated` is a TERMINAL state for the loader, not a step on the way to
   * `ready`: the account has no Character row, and only the player can say
   * which body it gets. `bootstrap.ts` mounts the chooser on it and
   * `createCharacter` moves it on. See `loadGameState`.
   */
  status: 'idle' | 'loading' | 'uncreated' | 'ready' | 'error';
  error: string | null;
  character: CharacterSheet | null;
  /** Client timestamp (Date.now()) the current `character` snapshot was fetched/pushed — energy extrapolation anchor. */
  characterFetchedAt: number;
  health: HealthSheet | null;
  inventory: InventorySheet | null;
  equipment: EquipmentSheet | null;
  /** Last `inventory:transferRejected` push, if any — cleared on the next successful inventory update. */
  inventoryRejection: InventoryRejection | null;
}

const INITIAL_STATE: GameState = {
  status: 'idle',
  error: null,
  character: null,
  characterFetchedAt: 0,
  health: null,
  inventory: null,
  equipment: null,
  inventoryRejection: null,
};

export const gameStateStore = createStore<GameState>({ ...INITIAL_STATE });

/**
 * Extrapolates a display-only energy value between server snapshots. Pure so
 * it's unit-testable without a clock or network. `now`/`fetchedAt` are
 * client-side Date.now() values — clock-skew clamped the same way active.md
 * mandates for server lazy-eval (`dt = max(0, now - calcAt)`).
 *
 * A running boost (the sheet's `energyBoost`, 2026-10-19) regenerates at
 * `mult` x until it ends, the plain rate after — the server's piecewise rule.
 */
export function extrapolateEnergy(
  energyAtFetch: number,
  regenPerMin: number,
  maxEnergy: number,
  fetchedAt: number,
  now: number,
  boost?: { until: string; mult: number } | null,
): number {
  const end = Math.max(fetchedAt, now);
  const boostEnd = boost ? Math.max(fetchedAt, Math.min(Date.parse(boost.until), end)) : fetchedAt;
  const boosted = ((boostEnd - fetchedAt) / 60_000) * regenPerMin * (boost?.mult ?? 1);
  const plain = ((end - boostEnd) / 60_000) * regenPerMin;
  return Math.min(maxEnergy, energyAtFetch + boosted + plain);
}

/**
 * Creates the Character row for a preset the PLAYER picked.
 *
 * `POST /character` takes `presetId` and `colorScheme` and nothing else — no
 * name (the account's username is the survivor's name) and no attributes (the
 * backend grants the starting five itself and banks no free points at
 * creation). So those are the only two things the choosing screen may ask for;
 * see `character-select/index.ts`, which had a survivor-name field and a
 * six-stat buy panel that this endpoint would have silently discarded.
 *
 * The preset is validated server-side against the admin-tunable
 * `CharacterConfig.presetIds`, so an id this build offers and the server does
 * not surfaces as INVALID_PRESET rather than a white screen.
 * `characters.contract.test.ts` keeps the two lists in step.
 */
async function createCharacterRow(presetId: string, colorScheme: number): Promise<CharacterSheet> {
  try {
    return await api.post<CharacterSheet>('/character', { presetId, colorScheme });
  } catch (err) {
    // Two tabs racing on first login both see UNCREATED; the loser gets 409 —
    // that's success, just re-read what the winner created.
    if (err instanceof ApiError && err.status === 409) {
      return api.get<CharacterSheet>('/character');
    }
    throw err;
  }
}

/**
 * The three sheets that only make sense once a Character row exists.
 *
 * Fetched after creation is confirmed, not alongside it — Equipment's blend
 * needs a character to blend against.
 */
async function loadSheetsFor(character: CharacterSheet): Promise<void> {
  const [health, inventory, equipment] = await Promise.all([
    api.get<HealthSheet>('/character/health'),
    api.get<InventorySheet>('/character/inventory'),
    api.get<EquipmentSheet>('/equipment'),
  ]);
  gameStateStore.set({
    status: 'ready', error: null, character, characterFetchedAt: Date.now(), health, inventory, equipment, inventoryRejection: null,
  });
}

/**
 * Fetches the survivor and, if there is one, the three sheets that hang off it.
 *
 * ─── An account with no character STOPS here ────────────────────────────────
 * This used to auto-create one: a fixed preset, posted silently, on the way
 * past. The comment that did it said "no creation UI in this story", and the
 * effect was that the choosing screen `character-select/` had been carrying
 * all along was unreachable — every new player was handed body `char-1`
 * without being asked. Reported 2026-09-18 as *"the character choosing step is
 * completely gone from the flow for new characters"*, which it was.
 *
 * So the load now reports `status: 'uncreated'` and returns, leaving the
 * decision to whoever owns the screen (`bootstrap.ts`), which mounts the
 * chooser and calls `createCharacter` with what the player picked. Keeping
 * that choice OUT of here matters: this module is imported by the probe
 * harness and by every panel that refreshes state, and a loader that creates
 * rows as a side effect of being called is how the screen got skipped in the
 * first place.
 */
export async function loadGameState(): Promise<void> {
  gameStateStore.set((s) => ({ ...s, status: 'loading', error: null }));
  try {
    const character = await api.get<CharacterSheet>('/character');
    if (character.state === 'UNCREATED') {
      gameStateStore.set((s) => ({
        ...s, status: 'uncreated', error: null, character, characterFetchedAt: Date.now(),
      }));
      return;
    }
    await loadSheetsFor(character);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load survivor state';
    gameStateStore.set((s) => ({ ...s, status: 'error', error: message }));
    throw err;
  }
}

/**
 * Creates the survivor the player chose, then loads the rest of the state.
 *
 * Leaves the store on `error` and rethrows on failure, exactly as
 * `loadGameState` does, so the chooser can keep the player on the screen with
 * their pick intact rather than dropping them into a half-loaded game.
 */
export async function createCharacter(presetId: string, colorScheme = 0): Promise<void> {
  gameStateStore.set((s) => ({ ...s, status: 'loading', error: null }));
  try {
    const character = await createCharacterRow(presetId, colorScheme);
    await loadSheetsFor(character);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create your survivor';
    gameStateStore.set((s) => ({ ...s, status: 'error', error: message }));
    throw err;
  }
}

let unsubscribers: Array<() => void> = [];

/**
 * Subscribes to GameGateway's push events to reconcile local state. Passive
 * energy regen never pushes on its own (no server tick — CharacterService
 * only emits on create/allocate/setColor/level-up), so a normal session may
 * never see a `character:updated` push; reconnect + tab-foreground are the
 * other two reconciliation points ("energy... reconciles to server").
 * Idempotent — safe to call again without a matching disconnect first.
 */
export function connectGameStateSync(): void {
  disconnectGameStateSync();

  const onCharacterUpdated = (sheet: CharacterSheet) => {
    gameStateStore.set((s) => ({ ...s, character: sheet, characterFetchedAt: Date.now() }));
  };
  const onHealthUpdated = (sheet: HealthSheet) => {
    gameStateStore.set((s) => ({ ...s, health: sheet }));
  };
  const onInventoryUpdated = (sheet: InventorySheet) => {
    gameStateStore.set((s) => ({ ...s, inventory: sheet, inventoryRejection: null }));
  };
  // S5's gateway docstring: this carries `current`, a freshly-read server
  // sheet, specifically so the client can re-stage instead of trusting a now-
  // stale local preview. Write both the rejection reason and `current` in.
  const onInventoryRejected = (payload: { reason: string; message?: string; current: InventorySheet | null }) => {
    gameStateStore.set((s) => ({
      ...s,
      inventory: payload.current ?? s.inventory,
      inventoryRejection: { reason: payload.reason, message: payload.message },
    }));
  };
  const onEquipmentUpdated = (sheet: EquipmentSheet) => {
    gameStateStore.set((s) => ({ ...s, equipment: sheet }));
  };
  // 'downed' clears the carried bag (Inventory.clearCarried, joined into
  // Health's transaction) and resets HP in the same commit — cheapest correct
  // response is a full reload rather than hand-patching two stores.
  const onDowned = () => { loadGameState().catch(() => {}); };
  const onReconnect = () => { loadGameState().catch(() => {}); };
  const onVisible = () => {
    if (document.visibilityState === 'visible') loadGameState().catch(() => {});
  };

  socket.on('character:updated', onCharacterUpdated);
  socket.on('health:updated', onHealthUpdated);
  socket.on('health:downed', onDowned);
  socket.on('inventory:updated', onInventoryUpdated);
  socket.on('inventory:transferRejected', onInventoryRejected);
  socket.on('equipment:updated', onEquipmentUpdated);
  socket.io.on('reconnect', onReconnect);
  document.addEventListener('visibilitychange', onVisible);

  unsubscribers = [
    () => socket.off('character:updated', onCharacterUpdated),
    () => socket.off('health:updated', onHealthUpdated),
    () => socket.off('health:downed', onDowned),
    () => socket.off('inventory:updated', onInventoryUpdated),
    () => socket.off('inventory:transferRejected', onInventoryRejected),
    () => socket.off('equipment:updated', onEquipmentUpdated),
    () => socket.io.off('reconnect', onReconnect),
    () => document.removeEventListener('visibilitychange', onVisible),
  ];
}

export function disconnectGameStateSync(): void {
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
}

/**
 * Clears the store back to its initial state. Separate from
 * `disconnectGameStateSync()` — that function also runs internally at the
 * top of every `connectGameStateSync()` call (idempotent re-subscribe), and
 * resetting the store there would wipe out state `loadGameState()` just
 * populated moments before. Call this on sign-out, so a second account
 * signing in on the same tab never renders the previous player's sheets.
 */
export function resetGameState(): void {
  gameStateStore.set({ ...INITIAL_STATE });
}

// ─── Inventory / Equipment actions ─────────────────────────────────
//
// Single-item buttons hitting the same batch endpoints inventory.md designed
// for staged, multi-item triage (rule 6's all-or-nothing submit) — this is a
// legitimate use of those endpoints (arrays of any length, including one),
// but it is NOT that batched staging/triage flow. There is no "stage several
// moves, review, submit once" UI here. A future session building that should
// not assume it already exists.
//
// Rule 7 (packForRaid) is greedy and never rejects on capacity — an item that
// doesn't fit the carried dual cap just silently stays in HIDEOUT, no error,
// no `inventory:transferRejected`. Callers must compare weight against
// remaining capacity themselves if they want to warn before the click.

/** Hideout-only (equip source must be HIDEOUT — inventory.md rule 10); swaps the slot's current occupant out atomically server-side. */
export async function equipItem(userItemId: string, slot: EquipmentSlot): Promise<void> {
  const equipment = await api.post<EquipmentSheet>('/equipment/equip', { userItemId, slot });
  // Equip doesn't touch InventoryService's own events (different service/transaction),
  // so the hideout list won't drop the now-equipped item without this refetch.
  const inventory = await api.get<InventorySheet>('/character/inventory');
  gameStateStore.set((s) => ({ ...s, equipment, inventory }));
}

export async function unequipItem(slot: EquipmentSlot): Promise<void> {
  const equipment = await api.post<EquipmentSheet>('/equipment/unequip', { slot });
  const inventory = await api.get<InventorySheet>('/character/inventory');
  gameStateStore.set((s) => ({ ...s, equipment, inventory }));
}

/** CARRIED -> HIDEOUT. Fire-and-forget over the socket; result arrives via `inventory:updated` or `inventory:transferRejected`. */
export function storeItem(userItemId: string): void {
  socket.emit('inventory:moveToHideout', { move: [userItemId], discard: [] });
}

/** HIDEOUT -> CARRIED (rule 7 — greedy, see module docstring above). */
export function packItem(userItemId: string): void {
  socket.emit('inventory:packForRaid', { itemIds: [userItemId] });
}

/** Hideout-only discard (matches `discardHideoutItems`'s own restriction). */
export function discardItem(userItemId: string): void {
  socket.emit('inventory:discard', { itemIds: [userItemId] });
}

/**
 * Consumes one `usable` item (HP_RESTORE or ENERGY_BOOST — see `InventoryItemSummary.usable`).
 * No manual refetch here unlike `equipItem`: `ConsumableService.useItem` already
 * calls Health/Character/Inventory's own `refreshAndEmit`, which push
 * `health:updated`/`character:updated`/`inventory:updated` over the socket —
 * the same subscriptions this module already wires up (see `connectGameStateSync`).
 */
export async function useItem(userItemId: string): Promise<UseItemResult> {
  return api.post<UseItemResult>('/consumable/use', { userItemId });
}

/**
 * Instantly refills energy to max for a Gold cost. Unlike `useItem`, the
 * backend route does NOT push a socket event (it's a plain debit + setEnergy,
 * no `refreshAndEmit` call) — so this merges the response into `gameStateStore`
 * itself, same idiom as `allocateAttribute`. `characterFetchedAt` is reset to
 * now so the HUD's client-side energy extrapolation re-anchors from the fresh
 * value instead of continuing to extrapolate from the pre-refill baseline.
 */
export async function refillEnergyPremium(): Promise<EnergyRefillResult> {
  const result = await api.post<EnergyRefillResult>('/character/energy/refill');
  gameStateStore.set((s) => (s.character?.state === 'ACTIVE' ? {
    ...s,
    character: {
      ...s.character,
      energy: result.energy,
      maxEnergy: result.maxEnergy,
      softBalance: result.cash,
      premiumBalance: result.gold,
    },
    characterFetchedAt: Date.now(),
  } : s));
  return result;
}

/**
 * Spends one banked level-up point on an attribute. The endpoint has shipped
 * since the character module landed and had **no caller** — banked points were
 * unspendable through the UI, so a level-up produced a number that nothing
 * could consume. `POST /character/allocate` rejects with `NO_ATTRIBUTE_POINTS`
 * or `ATTRIBUTE_MAXED` (both 400), and the attribute cap lives in
 * `CharacterConfig` server-side and is NOT on the sheet — so the client can
 * pre-disable on "no points" but cannot know the per-attribute ceiling, and
 * has to surface `ATTRIBUTE_MAXED` from the response instead of predicting it.
 *
 * The guarded `updateMany` behind this is atomic (`attributePoints: { gt: 0 }`),
 * so a double-click cannot overspend — the second call 400s.
 *
 * Returns the whole refreshed sheet; the server also emits its own update, but
 * setting it here means the panel repaints on the click rather than on the
 * socket round-trip.
 *
 * **Refetches equipment too**, for the same reason `equipItem` refetches
 * inventory: the derived stats the UI shows are `equipment.blendedStats`, not
 * `character.stats` (see this module's docstring), and they are computed by a
 * different service that `CharacterService.emitUpdate` does not touch. Without
 * this, spending a point on STR visibly raised the attribute while Carry (kg)
 * beside it stayed on the pre-spend number until something else happened to
 * refresh the sheet — caught by looking at the panel, not by a test, since the
 * frontend suite mocks the API and would have accepted either behaviour.
 */
export async function allocateAttribute(attribute: AttributeCode): Promise<void> {
  const character = await api.post<CharacterSheet>('/character/allocate', { attribute });
  const equipment = await api.get<EquipmentSheet>('/equipment');
  gameStateStore.set((s) => ({ ...s, character, equipment }));
}

/**
 * Spends a whole STAGED allocation — Janeks: *"add skill points and then hit
 * 'save' to apply."*
 *
 * ONE request, never a loop over `allocateAttribute`. The batch route spends
 * the set in a single all-or-nothing transaction; an N-call client loop can hit
 * `ATTRIBUTE_MAXED` on call 3 of 5 and leave the first two points spent with
 * nothing to roll them back, which is the exact reason that route exists.
 *
 * Refetches equipment for the same reason the single route does — the numbers
 * under the attributes are `equipment.blendedStats`, computed by a service the
 * character update does not touch.
 */
export async function allocateAttributes(
  staged: Partial<Record<AttributeCode, number>>,
): Promise<void> {
  // Zeroes are dropped rather than sent: the DTO is whitelisted and would
  // accept them, but "STR: 0" in the body describes a spend that is not
  // happening.
  const body: Record<string, number> = {};
  for (const [code, n] of Object.entries(staged)) if (n > 0) body[code] = n;
  const character = await api.post<CharacterSheet>('/character/allocate/batch', body);
  const equipment = await api.get<EquipmentSheet>('/equipment');
  gameStateStore.set((s) => ({ ...s, character, equipment }));
}
